import "@fontsource/poppins/400.css"
import "@fontsource/poppins/500.css"
import "@fontsource/poppins/600.css"
import "@fontsource/poppins/700.css"
import { createTheme, type TypographyStyle } from "@mui/material"
import type {} from "@mui/x-date-pickers-pro/themeAugmentation"

import SvgRadioSelected from "../assets/RadioSelected"
import SvgRadioUnselected from "../assets/RadioUnselected"
import CheckboxChecked from "../assets/svg-components/CheckboxChecked"
import CheckboxIndeterminate from "../assets/svg-components/CheckboxIndeterminate"
import CheckboxUnchecked from "../assets/svg-components/CheckboxUnchecked"

export const FONT_FAMILY = "Poppins"
export const BORDER_RADIUS = 2

declare module "@mui/material/styles" {
  interface TypographyVariants {
    display: TypographyStyle
    h1: TypographyStyle
    h2: TypographyStyle
    h2Bold: TypographyStyle
    h3: TypographyStyle
    h3Bold: TypographyStyle
    h4: TypographyStyle
    body1: TypographyStyle
    body1Bold: TypographyStyle
    body2: TypographyStyle
    body2Bold: TypographyStyle
    small1: TypographyStyle
    small1Bold: TypographyStyle
    tiny: TypographyStyle
    tinyLink: TypographyStyle
  }

  interface TypographyVariantsOptions {
    display: TypographyStyle
    h1: TypographyStyle
    h2: TypographyStyle
    h2Bold: TypographyStyle
    h3: TypographyStyle
    h3Bold: TypographyStyle
    h4: TypographyStyle
    body1: TypographyStyle
    body1Bold: TypographyStyle
    body2: TypographyStyle
    body2Bold: TypographyStyle
    small1: TypographyStyle
    small1Bold: TypographyStyle
    tiny: TypographyStyle
    tinyLink: TypographyStyle
  }
}

declare module "@mui/material/Typography" {
  interface TypographyPropsVariantOverrides {
    display: true
    h1: true
    h2: true
    h2Bold: true
    h3: true
    h3Bold: true
    h4: true
    body1: true
    body1Bold: true
    body2: true
    body2Bold: true
    small1: true
    small1Bold: true
    tiny: true
    tinyLink: true
  }
}

declare module "@mui/material/Button" {
  interface ButtonPropsVariantOverrides {
    destructive: true
  }
}

export const extraColors = {
  risk: {
    low: "rgba(159, 234, 212, 1)",
    moderate: "rgba(41, 209, 159, 1)",
    medium: "rgba(18, 155, 103, 1)",
    high: "rgba(0, 107, 71, 1)",
    severe: "rgba(31, 48, 51, 1)",
  },
  status: {
    greenMuted: "rgba(35, 73, 68, 1)",
    greenDeep: "rgba(31, 48, 51, 1)",
    greenDark: "rgba(0, 107, 71, 1)",
    green: "rgba(18, 155, 103, 1)",
    greenLight: "rgba(41, 209, 159, 1)",
    greenSubtle: "rgba(159, 234, 212, 1)",
    yellowMuted: "rgba(93, 78, 44, 1)",
    yellowDeep: "rgba(52, 50, 42, 1)",
    yellowDark: "rgba(157, 98, 0, 1)",
    yellowMiddle: "rgba(204, 151, 47, 1)",
    yellowLight: "rgba(255, 206, 100, 1)",
    orangeMuted: "rgba(93, 61, 41, 1)",
    orangeDeep: "rgba(52, 44, 41, 1)",
    orangeDark: "rgba(180, 56, 0, 1)",
    orangeMiddle: "rgba(225, 93, 34, 1)",
    orangeLight: "rgba(255, 169, 108, 1)",
    redMuted: "rgba(82, 33, 41, 1)",
    redDeep: "rgba(48, 34, 41, 1)",
    redDark: "rgba(160, 0, 0, 1)",
    redMiddle: "rgba(188, 15, 26, 1)",
    redLight: "rgba(255, 75, 85, 1)",
    redSubtle: "rgba(250, 168, 172, 1)",
    redGhost: "rgba(253, 229, 230, 1)",
    navyMuted: "rgba(37, 75, 101, 1)",
    navyDeep: "rgba(32, 49, 63, 1)",
    navyDark: "rgba(24, 52, 94, 1)",
    navy: "rgba(40, 115, 174, 1)",
    navyLight: "rgba(55, 178, 255, 1)",
    navyLightTransparent: "rgba(55, 178, 255, 0.2)",
    navySubtle: "rgba(169, 222, 255, 1)",
  },
  dark: "rgba(29, 34, 41, 1)",
  dark5: "rgba(40, 45, 52, 1)",
  dark10: "rgba(51, 56, 62, 1)",
  dark20: "rgba(93, 97, 101, 1)",
  medium: "rgba(175, 184, 193, 1)",
  hint: "rgba(103, 119, 137, 1)",
  disabled: "rgba(72, 86, 103, 1)",
  light: "rgba(243, 244, 246, 1)",
  subtle: "rgba(248, 249, 250, 1)",
  white: "rgba(255, 255, 255, 1)",
  translucentWhite: "rgba(255, 255, 255, 0.1)",
  translucent5: "rgba(255, 255, 255, 0.05)",
  translucent8: "rgba(255, 255, 255, 0.08)",
  translucent50: "rgba(255, 255, 255, 0.5)",
  translucentActive: "rgba(55, 178, 255, 0.2)",
  translucentBackgroundOverlay: "rgba(40, 45, 52, 0.7)",
  transparent: "rgba(0, 0, 0, 0)",
  black: "rgba(0, 0, 0, 1)",
  black10: "rgba(0, 0, 0, 0.1)",
  shadow: "rgba(0, 0, 0, 0.20)",
  purpleLight: "rgba(205, 55, 255, 1)",
  purpleSubtle: "rgba(249, 236, 255, 1)",
  purpleMiddle: "rgba(117, 0, 139, 1)",
  purpleDark: "rgba(81, 0, 96, 1)",
  purpleDeep: "rgba(48, 46, 60, 1)",
  purpleMuted: "rgba(82, 67, 94, 1)",
} as const

export const theme = createTheme({
  palette: {
    mode: "dark",
    primary: {
      main: extraColors.status.navyLight,
    },
    secondary: {
      main: extraColors.light,
    },
    info: {
      main: extraColors.status.navyDark,
    },
    text: {
      primary: extraColors.dark,
      secondary: extraColors.white,
      disabled: extraColors.disabled,
    },
    background: {
      default: extraColors.dark,
      paper: extraColors.translucent5,
    },
  },
  typography: {
    fontFamily: FONT_FAMILY,
    fontSize: 16,
    allVariants: {
      color: extraColors.white,
    },
    display: {
      fontSize: 50,
      fontWeight: 600,
    },
    h1: {
      fontSize: 36,
      fontWeight: 600,
    },
    h2: {
      fontSize: 26,
      fontWeight: 400,
    },
    h2Bold: {
      fontSize: 26,
      fontWeight: 600,
    },
    h3: {
      fontSize: 20,
      letterSpacing: "0.2px",
      fontWeight: 600,
    },
    h3Bold: {
      fontSize: 20,
      fontWeight: 600,
      color: extraColors.white,
    },
    h4: {
      fontSize: 24,
      letterSpacing: "0.1px",
      fontWeight: 600,
    },
    body1: {
      fontSize: 16,
      fontWeight: 400,
      color: extraColors.white,
    },
    body1Bold: {
      fontSize: 16,
      fontWeight: 600,
      color: extraColors.white,
    },
    body2: {
      fontSize: 14,
      fontWeight: 400,
      color: extraColors.white,
    },
    body2Bold: {
      fontSize: 14,
      fontWeight: 600,
      color: extraColors.white,
    },
    small1: {
      fontSize: 12,
      fontWeight: 400,
      color: extraColors.white,
    },
    small1Bold: {
      fontSize: 12,
      fontWeight: 600,
      color: extraColors.white,
    },
    tiny: {
      fontSize: 9,
      fontWeight: 600,
      color: extraColors.white,
    },
    tinyLink: {
      fontSize: 9,
      fontWeight: 600,
      color: extraColors.status.navyLight,
      textDecoration: "underline",
    },
  },
  components: {
    MuiTextField: {
      defaultProps: {
        size: "small",
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backdropFilter: "blur(8px)",
          backgroundColor: extraColors.translucentBackgroundOverlay,
          backgroundImage: "none",
          border: `1px solid ${extraColors.translucentWhite}`,
          boxShadow: "none",
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "contained", color: "primary" },
          style: {
            backgroundColor: extraColors.status.navyLight,
            color: extraColors.dark,
            "&:hover": {
              boxShadow: `0 0 7px 1px ${extraColors.translucentActive}`,
            },
            "&:focus-visible, &:focus": {
              outline: `2px solid ${extraColors.hint}`,
              outlineOffset: 2,
            },
            "&:active": {
              backgroundColor: extraColors.status.navySubtle,
            },
            "&:disabled": {
              backgroundColor: extraColors.hint,
              color: extraColors.medium,
              outline: "none",
            },
          },
        },
        {
          props: { variant: "destructive" },
          style: {
            border: `1px solid ${extraColors.status.redLight}`,
            backgroundColor: "transparent",
            color: extraColors.status.redLight,
            paddingLeft: 24,
            paddingRight: 24,
            "&:hover": {
              backgroundColor: extraColors.status.redDeep,
            },
            "&:focus, &:focus-visible": {
              backgroundColor: extraColors.status.redDeep,
              outline: `2px solid ${extraColors.hint}`,
              outlineOffset: 2,
            },
            "&:active": {
              backgroundColor: extraColors.status.redMuted,
              border: `1px solid ${extraColors.status.redMiddle}`,
              color: extraColors.status.redMiddle,
            },
            "&.Mui-disabled": {
              border: `1px solid ${extraColors.disabled}`,
              color: extraColors.disabled,
            },
          },
        },
        {
          props: { variant: "outlined", color: "primary" },
          style: {
            border: `1px solid ${extraColors.status.navyLight}`,
            color: extraColors.status.navyLight,
            backgroundColor: "transparent",
            paddingLeft: 24,
            paddingRight: 24,
            "&:hover": {
              backgroundColor: extraColors.translucent5,
              boxShadow: `0 0 7px 1px ${extraColors.translucentActive}`,
            },
            "&:focus, &:focus-visible": {
              backgroundColor: extraColors.translucent5,
              boxShadow: `0 0 7px 1px ${extraColors.translucentActive}`,
              outline: `2px solid ${extraColors.hint}`,
              outlineOffset: 2,
            },
            "&:active": {
              border: `1px solid ${extraColors.status.navySubtle}`,
              color: extraColors.status.navySubtle,
            },
            "&.Mui-disabled": {
              border: `1px solid ${extraColors.disabled}`,
              color: extraColors.disabled,
              backgroundColor: extraColors.translucent5,
            },
          },
        },
        {
          props: { variant: "text", color: "secondary" },
          style: {
            color: extraColors.white,
            "&:hover": {
              color: extraColors.white,
              backgroundColor: "transparent",
            },
          },
        },
      ],
      defaultProps: {
        disableRipple: true,
      },
      styleOverrides: {
        root: {
          textTransform: "none",
          fontFamily: FONT_FAMILY,
          borderRadius: 2,
          fontWeight: 600,
          fontSize: 16,
          minHeight: 40,
          padding: "7px 20px",
          border: "none",
          boxShadow: "none",
          "&:hover": {
            boxShadow: "none",
          },
        },
        text: {
          color: extraColors.status.navyLight,
          fontWeight: 600,
          fontFamily: FONT_FAMILY,
          fontSize: 14,
          letterSpacing: 0.5,
          padding: 8,
          outline: "none",
          border: "none",
          "&:hover": {
            color: extraColors.status.navyLight,
            backgroundColor: extraColors.translucentWhite,
          },
          "&:focus, &:focus-visible": {
            color: extraColors.status.navyLight,
            backgroundColor: "transparent",
          },
          "&:active": {
            color: extraColors.status.navySubtle,
            textDecorationColor: extraColors.status.navySubtle,
            outline: "none",
            border: "none",
          },
        },
      },
    },
    MuiMenu: {
      styleOverrides: {
        list: {
          padding: "8px 0",
        },
        root: {
          padding: 0,
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          padding: "2px 16px",
          minHeight: "fit-content",
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: extraColors.medium,
          "&.Mui-focused": {
            color: extraColors.white,
          },
          "&.Mui-disabled": {
            color: extraColors.disabled,
          },
        },
      },
    },

    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          color: extraColors.white,
          borderRadius: 2,
          background: extraColors.dark10,
          ".MuiOutlinedInput-notchedOutline": {
            transition: "border 250ms ease",
            border: `1px solid ${extraColors.disabled}`,
          },
          "&:hover": {
            ".MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${extraColors.disabled}`,
            },
            color: extraColors.white,
            boxShadow: `0 0 7px 1px ${extraColors.translucentWhite}`,
          },
          "&.Mui-focused": {
            ".MuiOutlinedInput-notchedOutline": {
              border: `1px solid ${extraColors.status.navyLight}`,
              boxShadow: `0 0 7px 1px ${extraColors.translucentWhite}`,
            },
            backgroundColor: extraColors.status.navyDeep,
            label: {
              color: extraColors.disabled,
            },
          },
          "&.Mui-disabled": {
            "&:hover": {
              label: {
                color: extraColors.disabled,
              },
              ".MuiOutlinedInput-notchedOutline": {
                border: `1px solid ${extraColors.disabled}`,
              },
            },
          },
        },
      },
    },
    MuiButtonBase: {
      styleOverrides: {
        root: {
          ".MuiDateRangePickerDay-day": {
            backgroundColor: extraColors.status.navyDark,
          },
        },
      },
    },
    MuiDialog: {
      styleOverrides: {
        paper: {
          borderRadius: BORDER_RADIUS,
          minWidth: 444,
          height: "100%",
          maxHeight: 500,
          backgroundColor: extraColors.translucentBackgroundOverlay,
          backdropFilter: "blur(8px)",
        },
      },
    },
    MuiDialogTitle: {
      styleOverrides: {
        root: {
          display: "flex",
          justifyContent: "space-between",
          color: extraColors.white,
          fontSize: 18,
        },
      },
    },

    MuiFormGroup: {
      styleOverrides: {
        root: {
          fontSize: 14,
          ".MuiFormControlLabel-label": {
            fontSize: 14,
          },
        },
      },
    },

    MuiRadio: {
      defaultProps: {
        disableRipple: true,
        icon: <SvgRadioUnselected />,
        checkedIcon: <SvgRadioSelected fill={extraColors.status.navyLight} />,
      },
      styleOverrides: {
        root: {
          paddingRight: 6,
          ".body": {
            fontSize: 14,
          },
          backgroundColor: "transparent",
          ".MuiTypography-body": {
            outline: `2px solid ${extraColors.status.navyLight}`,
          },
          "&:focus": {
            outline: `2px solid ${extraColors.status.navyLight}`,
          },
          "&:focus-visible": {
            outline: `2px solid ${extraColors.status.navyLight}`,
          },
          "&:hover": {
            backgroundColor: "transparent",
          },
          "&.Mui-checked": {
            backgroundColor: "transparent",
            "&:hover": {
              backgroundColor: "transparent",
            },
          },
          ".MuiFormControlLabel-label": {
            fontSize: 14,
            marginLeft: 10,
          },
        },
      },
    },
    MuiCheckbox: {
      defaultProps: {
        disableRipple: true,
        checkedIcon: <CheckboxChecked />,
        icon: <CheckboxUnchecked />,
        indeterminateIcon: <CheckboxIndeterminate />,
        focusVisibleClassName: "Mui-focusVisible",
        disableFocusRipple: true,
      },
      styleOverrides: {
        root: {
          "&.Mui-disabled": {
            opacity: 0.4,
          },
          "&.Mui-focusVisible svg": {
            boxShadow: `0 0 7px 1px ${extraColors.translucentWhite}`,
          },
        },
      },
    },
    MuiAlert: {
      styleOverrides: {
        root: {
          "& .MuiAlert-icon": {
            fontSize: 33,
            margin: "auto 12px auto 0",
          },
        },
      },
      variants: [
        {
          props: { severity: "warning", color: "warning" },
          style: {
            backgroundColor: extraColors.status.yellowMiddle,
            "& .MuiAlert-icon": {
              color: extraColors.status.yellowMiddle,
            },
          },
        },
      ],
    },
    MuiPopper: {
      // map pie chart labels were fighting zIndexes
      styleOverrides: {
        root: {
          zIndex: 9999,
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          color: extraColors.white,
          fontWeight: 600,
          fontSize: "14px",
          "&.Mui-selected": {
            color: extraColors.status.navyLight,
          },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: {
          backgroundColor: extraColors.status.navyLight,
        },
      },
    },
    MuiUseMediaQuery: {
      defaultProps: {
        noSsr: true,
      },
    },
    MuiSelect: {
      styleOverrides: {
        icon: {
          color: extraColors.status.navyLight,
        },
      },
    },
    MuiLink: {
      styleOverrides: {
        root: {
          color: extraColors.status.navyLight,
        },
      },
    },
    MuiPickerDay: {
      styleOverrides: {
        root: {
          color: extraColors.white,
        },
      },
    },
    MuiPickersSectionList: {
      styleOverrides: {
        sectionContent: {
          color: extraColors.white,
        },
      },
    },
    MuiPickersInputBase: {
      styleOverrides: {
        root: {
          color: extraColors.white,
        },
      },
    },
  },
})
